/* Superluxe Vuori-style menu (preview 2026-10-17; hover controller 2026-10-18).
   Desktop hover is driven here instead of Impact's per-item mouseover/mouseout (which we block):
   - first open after a 140ms hover-intent delay; moving onto another dropdown item switches instantly
   - the nav row, the gaps between items and the band down to the panel are one hover zone, so the gaps never close the menu
   - the menu closes 180ms after the pointer leaves that zone and the open panel (a plain link like ABOUT US counts as leaving)
   - a panel that is closing can't be hovered (it used to sit on top of the next panel during its fade when moving right-to-left
     and re-open itself), and a switch hides the old panel at once and shows the new one at full opacity: no cross-fade flicker
   - if Impact's async close finishes after a re-open it would drop the [open] attribute; we put it back
   Keyboard: Enter/Space/ArrowDown open, Esc closes and returns focus. Touch/click unchanged (Impact). */
(() => {
  const OPEN_DELAY = 140, CLOSE_DELAY = 180, SLOP = 8;
  const SEL = '.header__link-list details[is="mega-menu-disclosure"], .header__link-list details[is="dropdown-disclosure"]';
  const desktop = () => window.matchMedia('screen and (pointer: fine)').matches && window.matchMedia('(min-width: 1150px)').matches;
  let items = [], current = null, openT = null, openFor = null, closeT = null;
  const isHover = (d) => d.getAttribute('trigger') === 'hover';
  const findOpenDesc = (o) => { while (o && !Object.getOwnPropertyDescriptor(o, 'open')) o = Object.getPrototypeOf(o); return o && Object.getOwnPropertyDescriptor(o, 'open'); };
  const cancelOpen = () => { clearTimeout(openT); openT = null; openFor = null; };
  const cancelClose = () => { clearTimeout(closeT); closeT = null; };
  const openNow = (d) => {
    cancelOpen(); cancelClose();
    const prev = items.find((x) => x !== d && x.open);
    if (prev) { prev.classList.add('sl-mm-swap'); d.classList.add('sl-mm-swapin'); }
    d.open = true;   // Impact closes the open sibling itself
  };
  const closeAll = () => { cancelOpen(); cancelClose(); items.forEach((x) => { if (x.open) x.open = false; }); };
  const scheduleClose = () => { cancelOpen(); if (!closeT && items.some((x) => x.open)) closeT = setTimeout(() => { closeT = null; closeAll(); }, CLOSE_DELAY); };

  // Where is the pointer? -> {kind: 'trigger'|'zone'|'out', d}
  const where = (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return { kind: 'out' };
    const open = items.find((x) => x.open);
    if (open && open.lastElementChild.contains(t)) return { kind: 'zone' };            // inside the open panel
    const nav = items[0] && items[0].closest('.header__link-list');
    if (!nav) return { kind: 'out' };
    const s = t.closest('summary');
    const d = s && items.find((x) => x.summaryElement === s || x.firstElementChild === s);
    const row = nav.querySelector(':scope > ul').getBoundingClientRect();
    const lis = [...nav.querySelectorAll(':scope > ul > li')].map((li) => li.getBoundingClientRect()).filter((r) => r.width);
    if (!lis.length) return { kind: 'out' };
    const top = Math.min(...lis.map((r) => r.top)), bottom = Math.max(...lis.map((r) => r.bottom));
    if (d) return { kind: e.clientY <= bottom + 4 ? 'trigger' : 'zone', d };
    if (t.closest('.header__link-list > ul > li > a')) return { kind: 'out' };       // plain link (ABOUT US): counts as leaving
    // continuous band: from just above the row, across the gaps, down to the panel top / header bottom
    const header = nav.closest('.header') || nav.closest('header') || nav;
    const left = Math.min(...lis.map((r) => r.left)) - SLOP, right = Math.max(...lis.map((r) => r.right)) + SLOP;
    const bandBottom = open ? open.lastElementChild.getBoundingClientRect().top + 2 : bottom + SLOP;
    const bandTop = Math.min(top, row.top || top) - SLOP;
    if (e.clientX >= left && e.clientX <= right && e.clientY >= bandTop && e.clientY <= Math.max(bandBottom, header.getBoundingClientRect().bottom + 2) && open) return { kind: 'zone' };
    if (e.clientX >= left && e.clientX <= right && e.clientY >= bandTop && e.clientY <= bottom + SLOP) return { kind: 'zone' };
    return { kind: 'out' };
  };
  const onMove = (e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    if (!items.length || !desktop()) return;
    const w = where(e);
    const open = items.find((x) => x.open);
    if (w.kind === 'trigger' && isHover(w.d)) {
      cancelClose();
      if (w.d.open) { cancelOpen(); return; }
      if (open) { openNow(w.d); return; }                    // already showing a panel: switch immediately
      if (openFor !== w.d) { cancelOpen(); openFor = w.d; openT = setTimeout(() => { const d = openFor; openT = null; openFor = null; if (d) openNow(d); }, OPEN_DELAY); }
    } else if (w.kind === 'zone') {
      cancelClose(); if (openFor && !open) cancelOpen();      // gap/band: keep what's open, don't start opening from below
    } else {
      // leaving: close after CLOSE_DELAY, unless the open panel holds keyboard focus (keyboard users keep it until Esc/Tab out)
      if (open && !(open.contains(document.activeElement) && document.activeElement.matches(':focus-visible'))) scheduleClose(); else cancelOpen();
    }
  };
  document.addEventListener('pointermove', onMove, { passive: true, capture: true });
  document.addEventListener('mouseover', onMove, { passive: true, capture: true });
  document.documentElement.addEventListener('mouseleave', () => { if (desktop()) scheduleClose(); });
  window.addEventListener('blur', () => { cancelOpen(); });

  const init = () => {
    document.querySelectorAll(SEL).forEach((d) => {
      if (d.dataset.slMm || d.parentElement.closest('details')) return; // top-level only
      d.dataset.slMm = '1';
      items.push(d);
      // Block Impact's own hover open/close (instant open + 250/500ms close timers that raced with our timers)
      const block = (e) => { if (desktop() && isHover(d)) e.stopImmediatePropagation(); };
      d.addEventListener('mouseover', block, true);
      d.addEventListener('mouseout', block, true);
      // Wrap the instance's open setter: track closing panels (not hoverable) and instant swaps
      const desc = findOpenDesc(Object.getPrototypeOf(d));
      if (desc && desc.set) {
        Object.defineProperty(d, 'open', {
          configurable: true,
          get() { return desc.get.call(this); },
          set(v) {
            v = !!v;
            if (v) { this.classList.remove('sl-mm-off'); }
            else { this.classList.add('sl-mm-off'); this.classList.remove('sl-mm-swapin'); if (current === this) current = null; }
            desc.set.call(this, v);
            if (v) { current = this; this.classList.remove('sl-mm-swap'); }
          }
        });
      }
      // Impact's close awaits its fade, then removes [open] even if the menu was re-opened meanwhile: restore it
      new MutationObserver(() => {
        if (d.open && !d.hasAttribute('open')) d.setAttribute('open', '');
        if (!d.hasAttribute('open')) d.classList.remove('sl-mm-off', 'sl-mm-swap');
      }).observe(d, { attributes: true, attributeFilter: ['open'] });
      const summary = d.querySelector(':scope > summary');
      summary.addEventListener('keydown', (e) => {
        if (!['Enter', ' ', 'ArrowDown'].includes(e.key)) return;
        if (d.open && e.key !== 'ArrowDown') return; // open + Enter: Impact follows the item's link if it has one
        e.preventDefault();
        e.stopPropagation();
        cancelOpen(); cancelClose();
        if (!d.open) openNow(d);
        if (e.key === 'ArrowDown') {
          setTimeout(() => { const a = d.querySelector(':scope > :last-child a[href]'); if (a) a.focus(); }, 80);
        }
      });
    });
    items = items.filter((d) => d.isConnected);
  };
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = document.querySelector('.header__link-list > ul > li > details[open]');
    if (!open || !open.open) return;
    const hadFocus = open.contains(document.activeElement);
    cancelOpen(); cancelClose();
    open.open = false;
    const s = open.querySelector(':scope > summary');
    if (hadFocus && s) s.focus();
  }, true);
  // Equal panel height for all grouped mega menus: measure an image-free hidden clone of each panel (closed <details> can't be measured)
  const eqHeights = () => {
    if (!window.matchMedia('(min-width: 1150px)').matches) return;
    const panels = [...document.querySelectorAll('.header__link-list .sl-mm--groups')];
    if (panels.length < 2) return;
    document.documentElement.style.removeProperty('--sl-mm-inner-h');
    let max = 0;
    panels.forEach((m) => {
      const c = m.cloneNode(true);
      c.removeAttribute('id');
      c.querySelectorAll('img').forEach((i) => i.remove());
      c.setAttribute('aria-hidden', 'true');
      c.inert = true;
      c.style.cssText = 'position:fixed;left:0;top:0;width:100vw;display:block;visibility:hidden;opacity:0;pointer-events:none;z-index:-1';
      (m.closest('li') || document.body).appendChild(c);
      const inner = c.querySelector('.sl-mm__inner');
      if (inner) max = Math.max(max, inner.getBoundingClientRect().height);
      c.remove();
    });
    if (max) document.documentElement.style.setProperty('--sl-mm-inner-h', Math.ceil(max) + 'px');
  };
  let eqT;
  window.addEventListener('resize', () => { clearTimeout(eqT); eqT = setTimeout(eqHeights, 150); });
  window.addEventListener('load', eqHeights);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(eqHeights);
  document.addEventListener('shopify:section:load', eqHeights);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  window.addEventListener('load', init);
  document.addEventListener('shopify:section:load', init);
})();

/* How It Works animated tiles: play on panel open, tile hover and when the drawer row scrolls into view.
   No JS or reduced motion: the overlays stay in their final frame (CSS default). */
(() => {
  if (window.__slHiw) return; window.__slHiw = true;
  const rm = window.matchMedia('(prefers-reduced-motion: reduce)');
  const play = (t) => {
    if (rm.matches || !t) return;
    const now = Date.now();
    if (t._slHiwT && now - t._slHiwT < 6500) return;   // let a run finish before replaying
    t._slHiwT = now;
    t.classList.remove('is-play'); void t.offsetWidth; t.classList.add('is-play');
  };
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (d.tagName === 'DETAILS' && d.open) d.querySelectorAll('[data-sl-hiw]').forEach((t) => { t._slHiwT = 0; play(t); });
  }, true);
  document.addEventListener('mouseover', (e) => {
    const a = e.target.closest && e.target.closest('.sl-mm__tile-link');
    if (a) play(a.querySelector('[data-sl-hiw]'));
  }, true);
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((es) => es.forEach((en) => { if (en.isIntersecting && en.intersectionRatio > .6) play(en.target); }), { threshold: [0, .6, 1] });
    const watch = () => document.querySelectorAll('.sl-mm-drawer [data-sl-hiw]').forEach((t) => { if (!t._slIo) { t._slIo = 1; io.observe(t); } });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch); else watch();
    document.addEventListener('shopify:section:load', watch);
  }
})();
